import {useState} from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Radio,
  RadioGroup,
  RadioProps,
  Text,
  useToast,
} from '@chakra-ui/react';

import {py} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {ConfirmIcon} from '@eksab/assets/icons';
import {useRegionSettings} from '@eksab/hooks/useRegionSettings';

import type {CoinsPackage, PackageType} from '@eksab/features/store/types';
import type {BadgePackage} from '@eksab/features/store/badges/types';

import {OrderSummary, FawryPayment, EWalletPayment, CardPayment} from './';
import {useDiscountVoucherCodeQuery, type DiscountVoucher} from '../hooks';
import {EWalletIcon, FawryLogo, MasterCardIcon, ValULogo, VisaIcon} from '../icons';

type PaymentMethod = 'fawry' | 'cc' | 'e-wallet';

const ERROR_CODE_REDEEMED_VOUCHER = 1002;

export type PaymentProps =
  | {
      type: 'coin';
      pack: CoinsPackage;
    }
  | {
      type: 'badge';
      pack: BadgePackage;
    };

export function Payment(props: PaymentProps) {
  const {type, pack} = props;
  const {data: regionSettings} = useRegionSettings();
  const DEFAULT_PAYMENT_METHOD: PaymentMethod | null = !!regionSettings?.settings?.['payment_methods.wallets']
    ? 'e-wallet'
    : !!regionSettings?.settings?.['payment_methods.fawry']
      ? 'fawry'
      : !!regionSettings?.settings?.['payment_methods.cc']
        ? 'cc'
        : null;

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(DEFAULT_PAYMENT_METHOD);
  const [codeSuccess, setCodeSuccess] = useState(false);
  const [code, setCode] = useState('');
  const [applyBtnClicked, setApplyBtnClicked] = useState(false);
  const [discountVoucher, setDiscountVoucher] = useState<DiscountVoucher>();
  const toast = useToast();

  const t = useTranslate();

  useDiscountVoucherCodeQuery(
    {code, package_id: pack.id, package_type: type},
    {
      enabled: applyBtnClicked,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setDiscountVoucher(data);
        mixpanel?.track('Promo Code Redemption');
        setCodeSuccess(true);
      },
      onError: (e) => {
        toast({
          title: t(
            `payment.voucher.status.${
              e.response?.data?.code === ERROR_CODE_REDEEMED_VOUCHER ? 'already-redeemed' : 'invalid'
            }`,
          ),
          status: 'error',
        });

        setDiscountVoucher(undefined);
        setCodeSuccess(false);
      },
      onSettled: () => setApplyBtnClicked(false),
    },
  );

  const onCodeApply = () => {
    setCodeSuccess(false);

    if (code.length) setApplyBtnClicked(true);
    else toast({title: t('payment.voucher.status.required'), status: 'error'});
  };

  return (
    <Container py={py} flexDir="column" gap={[5, null, 10]}>
      <Heading as="h1" fontSize={['md', null, 'xl', '2rem']}>
        {t('payment')}
      </Heading>

      <Flex flexDir={['column', null, 'row']} justify="space-between" gap="6">
        {DEFAULT_PAYMENT_METHOD ? (
          <Flex as="section" w={['full', null, '28rem']} flexDir="column" gap="3">
            <Text as="h2" fontSize={['sm', null, 'md']} fontWeight="700">
              {t('payment.method.choose')}
            </Text>

            <RadioGroup
              w="full"
              display="flex"
              gap={[3, 5]}
              justifyContent="space-between"
              defaultValue={DEFAULT_PAYMENT_METHOD}
              onChange={(value: PaymentMethod) => setSelectedMethod(value)}
            >
              {!!regionSettings?.settings?.['payment_methods.wallets'] && (
                <PaymentMethodRadio value="e-wallet">
                  <Flex wrap="wrap" gap="3" align="center" justify="center" textAlign="center">
                    <EWalletIcon width={[8, 12]} height="auto" />

                    <Text fontSize={['xs', 'sm', 'md']} fontWeight="600" color="green.700">
                      {t('payment.method.e-wallet')}
                    </Text>
                  </Flex>
                </PaymentMethodRadio>
              )}

              {!!regionSettings?.settings?.['payment_methods.fawry'] && (
                <PaymentMethodRadio value="fawry">
                  <Image src={FawryLogo.src} alt="Fawry" w="full" minW="16" maxW="36" height="auto" />
                </PaymentMethodRadio>
              )}

              {regionSettings?.settings?.['payment_methods.cc'] && (
                <PaymentMethodRadio value="cc">
                  <Flex wrap="wrap" justify="center" align="center" gap={[5, null, 3]}>
                    <VisaIcon width={[14, null, null, 16]} height="auto" />
                    <MasterCardIcon width={[14, null, null, 16]} height="auto" />
                    <Image src={ValULogo.src} alt="ValU" w={[14, null, null, 16]} h="auto" />
                  </Flex>
                </PaymentMethodRadio>
              )}
            </RadioGroup>

            <GetPaymentMethod method={selectedMethod!} packageId={props.pack.id} code={code} type={props.type} />
          </Flex>
        ) : (
          <Text>{t('payment.no-payment-is-available')}</Text>
        )}

        <Flex w={['full', null, '23.75rem']} flexDir="column" gap="8">
          <Box as="section" w="full">
            <Text as="h2" fontSize={['sm', null, 'md']} fontWeight="700" mb="2.5">
              {t('payment.voucher.apply')}
            </Text>

            {codeSuccess ? (
              <Box
                w="full"
                display="flex"
                justifyContent="center"
                alignItems="center"
                borderRadius="6px"
                borderWidth="1px"
                borderColor="black"
                overflow="hidden"
              >
                <Box display="flex" py="2" px={[5, 3]} flex="1" justifyContent="space-between" alignItems="center">
                  <Text fontSize="sm" fontWeight="500">
                    {code}
                  </Text>

                  <ConfirmIcon />
                </Box>

                <Text
                  fontSize="sm"
                  fontWeight="500"
                  py="2.5"
                  px="6"
                  h="10"
                  borderStartWidth="1px"
                  borderStartColor="black"
                  onClick={() => setCodeSuccess(false)}
                  cursor="pointer"
                  transition="background-color 0.3s"
                  _hover={{bgColor: 'gray.200'}}
                >
                  {t('app.actions.edit')}
                </Text>
              </Box>
            ) : (
              <InputGroup w="full">
                <Input
                  fontSize="sm"
                  fontWeight="500"
                  borderRadius="sm"
                  placeholder={t('payment.voucher')}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />

                <InputRightElement borderEndRadius="sm" bgColor="green.400" w="20">
                  <Button
                    borderRadius="inherit"
                    borderEndRadius="6px"
                    fontSize="sm"
                    fontWeight="700"
                    h="full"
                    w="full"
                    textTransform="capitalize"
                    onClick={onCodeApply}
                  >
                    {t('app.actions.apply')}
                  </Button>
                </InputRightElement>
              </InputGroup>
            )}
          </Box>

          <OrderSummary {...props} voucher={discountVoucher} />
        </Flex>
      </Flex>
    </Container>
  );
}

const PaymentMethodRadio: React.FC<RadioProps> = ({children, ...props}) => (
  <Radio sx={{'~ *': {flex: 1}}} flex={1} spacing={[2, 3, 4]} size="lg" _focus={{}} colorScheme="green" {...props}>
    {children}
  </Radio>
);

interface GetPaymentMethodProps {
  method: PaymentMethod;
  packageId: number;
  code: string;
  type: PackageType;
}

const GetPaymentMethod = ({method, ...props}: GetPaymentMethodProps) => {
  switch (method) {
    case 'e-wallet':
      return <EWalletPayment {...props} />;
    case 'fawry':
      return <FawryPayment {...props} />;
    case 'cc':
      return <CardPayment {...props} />;
  }
};
